import { useEffect, useState } from 'react';
import { Eye, EyeOff, Settings2, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';

const VIS_STORE_KEY   = 'panorix-widget-visibility-v1';
const ORDER_STORE_KEY = 'panorix-widget-order-v1';

/**
 * Personalization for dashboard widgets — hide/show and reorder (drag-and-drop),
 * both persisted per user via localStorage, scoped per project + tab.
 *
 *   <WidgetSettingsGear projectId scope widgets={[{key,label},…]} />
 *   const order = useWidgetOrder(projectId, scope, ['a','b','c']);   // → reordered array
 *   <PersonalizableSlot projectId scope widgetKey ...>{...}</PersonalizableSlot>
 */

function _readMap(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); }
  catch { return {}; }
}
function _writeMap(key, m) { try { localStorage.setItem(key, JSON.stringify(m)); } catch { /* ignore */ } }

const _visKey    = (projectId, scope, widgetKey) => `${projectId || 'global'}::${scope}::${widgetKey}`;
const _orderKey  = (projectId, scope)            => `${projectId || 'global'}::${scope}`;

export function isWidgetHidden(projectId, scope, widgetKey) {
  return _readMap(VIS_STORE_KEY)[_visKey(projectId, scope, widgetKey)] === true;
}
export function setWidgetHidden(projectId, scope, widgetKey, hidden) {
  const m = _readMap(VIS_STORE_KEY);
  m[_visKey(projectId, scope, widgetKey)] = !!hidden;
  _writeMap(VIS_STORE_KEY, m);
  window.dispatchEvent(new Event('panorix-widget-visibility-change'));
}

function _readOrder(projectId, scope) {
  return _readMap(ORDER_STORE_KEY)[_orderKey(projectId, scope)] || null;
}
function _writeOrder(projectId, scope, order) {
  const m = _readMap(ORDER_STORE_KEY);
  m[_orderKey(projectId, scope)] = order;
  _writeMap(ORDER_STORE_KEY, m);
  window.dispatchEvent(new Event('panorix-widget-order-change'));
}

/**
 * Hook — returns the widget-key array in the user's saved order (falls back
 * to the default order if none saved). Auto-refreshes on order change.
 */
export function useWidgetOrder(projectId, scope, defaultOrder) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const onChange = () => setTick(t => t + 1);
    window.addEventListener('panorix-widget-order-change', onChange);
    return () => window.removeEventListener('panorix-widget-order-change', onChange);
  }, []);
  const saved = _readOrder(projectId, scope);
  if (!saved) return defaultOrder;
  const savedFiltered = saved.filter(k => defaultOrder.includes(k));
  const missing = defaultOrder.filter(k => !saved.includes(k));
  return [...savedFiltered, ...missing];
}

export function PersonalizableSlot({ projectId, scope, widgetKey, children }) {
  const [hidden, setHidden] = useState(() => isWidgetHidden(projectId, scope, widgetKey));
  useEffect(() => {
    const onChange = () => setHidden(isWidgetHidden(projectId, scope, widgetKey));
    window.addEventListener('panorix-widget-visibility-change', onChange);
    return () => window.removeEventListener('panorix-widget-visibility-change', onChange);
  }, [projectId, scope, widgetKey]);
  if (hidden) return null;
  return children;
}

/**
 * Gear popover — hide/show + drag-to-reorder widgets on the current tab.
 */
export function WidgetSettingsGear({ projectId, scope, widgets }) {
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => {
    const onChange = () => setTick(t => t + 1);
    window.addEventListener('panorix-widget-visibility-change', onChange);
    window.addEventListener('panorix-widget-order-change',      onChange);
    return () => {
      window.removeEventListener('panorix-widget-visibility-change', onChange);
      window.removeEventListener('panorix-widget-order-change',      onChange);
    };
  }, []);

  // Order applied to the panel list too
  const order = useWidgetOrder(projectId, scope, widgets.map(w => w.key));
  const ordered = order.map(k => widgets.find(w => w.key === k)).filter(Boolean);
  const hiddenCount = widgets.filter(w => isWidgetHidden(projectId, scope, w.key)).length;

  const moveTo = (fromKey, toIndex) => {
    const cur  = order.slice();
    const from = cur.indexOf(fromKey);
    if (from < 0 || toIndex < 0 || toIndex >= cur.length) return;
    cur.splice(from, 1);
    cur.splice(toIndex, 0, fromKey);
    _writeOrder(projectId, scope, cur);
  };
  const resetOrder = () => _writeOrder(projectId, scope, widgets.map(w => w.key));

  // HTML5 DnD handlers
  const onDragStart = (e, key) => { e.dataTransfer.setData('text/plain', key); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver  = (e)      => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop      = (e, toKey) => {
    e.preventDefault();
    const from = e.dataTransfer.getData('text/plain');
    if (!from || from === toKey) return;
    const toIdx = order.indexOf(toKey);
    moveTo(from, toIdx);
  };

  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(v => !v)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium hover:opacity-90"
              style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8' }}
              data-testid={`widget-settings-btn-${scope}`}
              title="Show / hide / reorder dashboard widgets">
        <Settings2 className="w-3.5 h-3.5" />
        Widgets{hiddenCount > 0 ? ` (${widgets.length - hiddenCount}/${widgets.length})` : ''}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 w-72 rounded-md shadow-lg border overflow-hidden"
               style={{ background: '#0A0E1A', borderColor: '#3A3F4A' }}
               data-testid={`widget-settings-panel-${scope}`}>
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-700/50 flex items-center gap-2">
              <span>Widgets — {scope}</span>
              <button onClick={resetOrder}
                      className="ml-auto normal-case tracking-normal text-[10px] text-slate-400 hover:text-slate-200"
                      data-testid={`widget-reset-order-${scope}`}>
                Reset order
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto py-1">
              {ordered.map((w, idx) => {
                const hidden = isWidgetHidden(projectId, scope, w.key);
                return (
                  <div key={w.key}
                       draggable
                       onDragStart={(e) => onDragStart(e, w.key)}
                       onDragOver={onDragOver}
                       onDrop={(e) => onDrop(e, w.key)}
                       className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-800/40 text-[11px] text-slate-200 border-b border-slate-800/30 cursor-move"
                       data-testid={`widget-row-${w.key}`}>
                    <GripVertical className="w-3 h-3 text-slate-600 flex-none" />
                    <button onClick={() => setWidgetHidden(projectId, scope, w.key, !hidden)}
                            className="flex items-center gap-1 flex-1 text-left"
                            data-testid={`widget-toggle-${w.key}`}>
                      {hidden
                        ? <EyeOff className="w-3 h-3 text-slate-500 flex-none" />
                        : <Eye    className="w-3 h-3 text-emerald-400 flex-none" />}
                      <span className="flex-1 truncate">{w.label}</span>
                    </button>
                    <button onClick={() => moveTo(w.key, Math.max(0, idx - 1))}
                            disabled={idx === 0}
                            className="p-0.5 rounded hover:bg-slate-700/50 disabled:opacity-30"
                            data-testid={`widget-move-up-${w.key}`}
                            title="Move up">
                      <ArrowUp className="w-3 h-3 text-slate-400" />
                    </button>
                    <button onClick={() => moveTo(w.key, Math.min(ordered.length - 1, idx + 1))}
                            disabled={idx === ordered.length - 1}
                            className="p-0.5 rounded hover:bg-slate-700/50 disabled:opacity-30"
                            data-testid={`widget-move-down-${w.key}`}
                            title="Move down">
                      <ArrowDown className="w-3 h-3 text-slate-400" />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="px-3 py-1.5 border-t border-slate-700/50 text-[10px] text-slate-500">
              Drag or use ↑↓ · Click eye to hide · Per user + project
            </div>
          </div>
        </>
      )}
    </div>
  );
}

